import type {
  AiAdvisorMsg,
  AiAdvisorQuery,
  AiAdvisorResponse,
  AiAdvisorRetrievalResult,
} from '@onshape/www-inertia';
import { ErrorTagUtil } from '@onshape/www-inertia';
import AntiSpamUtil from '~/hekate-www/external/util/AntiSpamUtil';
import ErrorUtil from '~/hekate-www/external/util/ErrorUtil';
import CatchUtil from '~/utils/CatchUtil';
import {
  getBooleanQueryParameter,
  getIntegerQueryParameter,
  getStringQueryParameter,
} from '~/utils/queryParamUtils';
import { useTypewriterAnimation } from '~/composables/useTypewriterAnimation';
import useEnvContext from '~/composables/useEnvContext';
import UxProfileUtil from '~/hekate-www/external/util/UxProfileUtil';

export type AdvisorSessionStorage = {
  query?: string;
  conversationId: string;
  numQueries?: number;
};

type AiAdvisorChatMsg = Omit<AiAdvisorMsg, 'content'> & { content: { text: string } };

type AiAdvisorChatEntry = {
  message: AiAdvisorChatMsg;
  retrievalResults: AiAdvisorRetrievalResult[];
  defaultVisibleRetrievalResults: number;
  totalRetrievalResults: number;
  visibleRetrievalCount: number;
  hasMoreRetrievalResults: boolean;
  isStreaming: boolean;
  needsEmailCapture: boolean;
};

type ParsedQueryResponse = ReturnType<typeof parseQueryResponse>;

// Analytics events emitted when a query is sent, by flow.
type AiAdvisorAnalyticsEvent = 'AiSearchInteraction' | 'AiSearchShowMoreResults' | 'AiSearchRetry';

// Analytics events emitted when a query fails, by flow.
type AiSearchFailureEvent =
  | 'AiSearchRequestFailed'
  | 'AiSearchShowMoreResultsFailed'
  | 'AiSearchRetryFailed'
  | 'AiSearchPrefetchFailed';

// Per-request overrides for the shared executeQuery flow.
type ExecuteQueryOptions = {
  queryText: string;
  analyticsEvent: AiAdvisorAnalyticsEvent;
  failureEvent: AiSearchFailureEvent;
  errorLogLabel: string;
  // Append the user's message to the chat history before querying (normal sends only).
  pushUserMessage?: boolean;
  // Drop the trailing assistant message before re-querying (retry only).
  popLastAssistant?: boolean;
  // Clear the input field once the request settles (normal sends only).
  clearQueryOnFinish?: boolean;
  // Extra fields merged into the request body (e.g. tryAgain, showMoreResults, retrievedContext).
  bodyOverrides?: {
    tryAgain?: boolean;
    showMoreResults?: boolean;
    retrievedContext?: AiAdvisorRetrievalResult[];
  };
  // Extra fields merged into the analytics event (e.g. isRetry).
  extraAnalytics?: Record<string, string>;
};

type TrackFailureOptions = {
  failureEvent: AiSearchFailureEvent;
  err: unknown;
  sentryLabel: string;
  sentryTag: Parameters<typeof ErrorUtil.safePostMessageToSentry>[0]['tag'];
};

const FREE_QUERY_LIMIT = 10;
const SOURCE_INCREMENT = 5;
const ANONYMOUS_EMAIL = 'anonymous@ptc.com';
const AI_SEARCH_MOUSEFLOW_ERROR_TAG = 'ai-search-error';
const AI_SEARCH_MOUSEFLOW_RATE_LIMIT_TAG = 'ai-search-rate-limited';

// fallback message values
export const RATE_LIMIT_MESSAGE =
  'You have reached your free quota for queries to Onshape Advisor. Please log in or sign up for Onshape to continue making requests.';

export const MISSING_TOKEN_MESSAGE =
  'Please try again later or contact support if the issue persists.';

export const REQUEST_ERROR_MESSAGE = 'Error making request';

const aiAdvisorSessionStorageKey = 'AiAdvisorInfo';
const aiAdvisorSessionStorageTtl = 1000 * 60 * 60; // 1 hour

// Shared state across all component instances
const advisorSessionId: Ref<string> = ref('');
const sessionEmail: Ref<string> = ref('');

const conversationStartPromise: Ref<Promise<void> | null> = ref(null);
const isQueryLoading: Ref<boolean> = ref(false);
const latestRequestId: Ref<number> = ref(0);
const lastStoppedRequestId: Ref<number> = ref(0);

const isRequestCancelled = ({ requestId }: { requestId: number }): boolean => {
  if (lastStoppedRequestId.value >= requestId) {
    return true;
  }

  return latestRequestId.value !== requestId;
};

// Builds the payload expected by the advisor API, including anti-spam metadata.
const buildQueryBody = ({
  queryText,
  sessionId,
  numQueries,
  csrfToken,
  email,
  tryAgain,
  showMoreResults,
  retrievedContext,
  clearRateLimits,
}: {
  queryText: string;
  sessionId: string;
  numQueries: number;
  csrfToken: string;
  email: string;
  tryAgain?: boolean;
  showMoreResults?: boolean;
  retrievedContext?: AiAdvisorRetrievalResult[];
  clearRateLimits?: boolean;
}): AiAdvisorQuery => {
  const shouldTryAgain = tryAgain ?? false;
  const shouldShowMoreResults = showMoreResults ?? false;
  const safeRetrievedContext = retrievedContext ?? [];

  return {
    msg: {
      role: 'user',
      content: { text: queryText },
    },
    sessionId,
    numQueries,
    csrfToken,
    signedScore: AntiSpamUtil.getSignedScore(),
    ...AntiSpamUtil.getTimings(),
    ...UxProfileUtil.getInteractions(),
    email,
    ...(shouldTryAgain && { tryAgain: shouldTryAgain }),
    ...(shouldShowMoreResults && { showMoreResults: shouldShowMoreResults }),
    ...(safeRetrievedContext.length > 0 && { retrievedContext: safeRetrievedContext }),
    ...(clearRateLimits && { clearRateLimits }),
  };
};

// Client-side friction only; server-side validation/rate limiting remains authoritative.
const checkRateLimit = ({
  loginStatus,
  numQueries,
  freeQueryLimit,
  rateLimitMessage,
}: {
  loginStatus: 'unknown' | 'loggedin' | 'loggedout';
  numQueries: number;
  freeQueryLimit: number;
  rateLimitMessage?: string;
}): { isRateLimited: boolean; message?: string } => {
  if (loginStatus === 'unknown') {
    return { isRateLimited: false };
  }

  if (loginStatus !== 'loggedin' && numQueries > freeQueryLimit) {
    return { isRateLimited: true, message: rateLimitMessage ?? RATE_LIMIT_MESSAGE };
  }

  return { isRateLimited: false };
};

// Normalizes optional API fields so UI code can render with a stable shape.
const parseQueryResponse = (response: AiAdvisorResponse) => ({
  responseText: response.response ?? 'No response received',
  conversationId: response.conversationId,
  hasMoreRetrievalResults: response.hasMoreRetrievalResults ?? false,
  humanHandoffSuggested: response.humanHandoffSuggested ?? false,
  retrievalResults: response.retrievalResults ?? [],
  totalRetrievalResults: response.totalRetrievalResults ?? 0,
  defaultVisibleRetrievalResults: response.defaultVisibleRetrievalResults ?? 0,
  detectedIntent: response.detectedIntent ?? '',
  detectedLanguage: response.detectedLanguage ?? '',
});

const getVisibleSourceCount = ({
  retrievalResults,
  defaultVisibleRetrievalResults,
  totalRetrievalResults,
}: {
  retrievalResults: AiAdvisorRetrievalResult[];
  defaultVisibleRetrievalResults: number;
  totalRetrievalResults: number;
}): number => {
  const safeDefaultVisible =
    defaultVisibleRetrievalResults > 0 ? defaultVisibleRetrievalResults : SOURCE_INCREMENT;
  const safeTotal = totalRetrievalResults > 0 ? totalRetrievalResults : retrievalResults.length;

  return Math.min(safeDefaultVisible, safeTotal, retrievalResults.length);
};

const createChatEntry = ({
  message,
  retrievalResults,
  defaultVisibleRetrievalResults,
  totalRetrievalResults,
  hasMoreRetrievalResults,
  isStreaming,
}: {
  message: AiAdvisorChatMsg;
  retrievalResults?: AiAdvisorRetrievalResult[];
  defaultVisibleRetrievalResults?: number;
  totalRetrievalResults?: number;
  hasMoreRetrievalResults?: boolean;
  isStreaming?: boolean;
}): AiAdvisorChatEntry => {
  const safeRetrievalResults = retrievalResults ?? [];
  const safeDefaultVisibleRetrievalResults = defaultVisibleRetrievalResults ?? SOURCE_INCREMENT;
  const safeTotalRetrievalResults = totalRetrievalResults ?? safeRetrievalResults.length;

  return {
    message,
    retrievalResults: safeRetrievalResults,
    defaultVisibleRetrievalResults: safeDefaultVisibleRetrievalResults,
    totalRetrievalResults: safeTotalRetrievalResults,
    visibleRetrievalCount: getVisibleSourceCount({
      retrievalResults: safeRetrievalResults,
      defaultVisibleRetrievalResults: safeDefaultVisibleRetrievalResults,
      totalRetrievalResults: safeTotalRetrievalResults,
    }),
    hasMoreRetrievalResults: hasMoreRetrievalResults ?? false,
    isStreaming: isStreaming ?? false,
    needsEmailCapture: false,
  };
};

const isValidEmailFormat = (email: string): boolean => {
  if (!email || email.length > 254) return false;
  if (/\s/.exec(email)) return false;
  if (email.includes('..')) return false;
  const parts = email.split('@');
  if (parts.length !== 2) return false;
  const localPart = parts[0] ?? '';
  if (!localPart) return false;
  const hostParts = (parts[1] ?? '').split('.');
  if (hostParts.length < 2) return false;
  if (hostParts.some((label) => !label)) return false;
  const tld = hostParts[hostParts.length - 1];
  if (!tld || tld.length < 2) return false;
  return true;
};

const createEmptyAssistantEntry = (): AiAdvisorChatEntry => {
  return createChatEntry({
    message: {
      role: 'assistant',
      content: { text: '' },
    },
    isStreaming: true,
  });
};

// Copies the parsed retrieval metadata onto a chat entry so the UI can paginate sources.
const applyRetrievalResults = ({
  entry,
  parsedResponse,
}: {
  entry: AiAdvisorChatEntry;
  parsedResponse: ParsedQueryResponse;
}): void => {
  entry.retrievalResults = parsedResponse.retrievalResults;
  entry.defaultVisibleRetrievalResults = parsedResponse.defaultVisibleRetrievalResults;
  entry.totalRetrievalResults = parsedResponse.totalRetrievalResults;
  entry.hasMoreRetrievalResults = parsedResponse.hasMoreRetrievalResults;
  entry.visibleRetrievalCount = getVisibleSourceCount({
    retrievalResults: parsedResponse.retrievalResults,
    defaultVisibleRetrievalResults: parsedResponse.defaultVisibleRetrievalResults,
    totalRetrievalResults: parsedResponse.totalRetrievalResults,
  });
};

/**
 * Composable for managing AI Advisor chat interactions.
 *
 * @param options.chatBox - Ref to the chat container used for auto-scrolling.
 * @param options.errorMessages - Optional overrides for user-facing error strings.
 */
export const useAiQuery = ({
  chatBox,
  errorMessages,
}: {
  chatBox?: Ref<HTMLElement | null>;
  errorMessages?: {
    rateLimitMessage?: string;
    requestErrorMessage?: string;
    missingTokenMessage?: string;
  };
}) => {
  const route = useRoute();
  const { mapFbFunction } = useServiceEndpoints();
  const { isLoggedIn, user } = useCrossSession();
  const { getFromSessionStorageOrNull, setToSessionStorage } = useWwwStorage();
  const { dataLayerPush, mouseFlowPush } = useTracking();

  const query = ref('');
  const chatHistory: Ref<AiAdvisorChatEntry[]> = ref([]);
  const trimmedQuery = computed(() => query.value.trim());
  const detectedIntent = ref('');
  const detectedLanguage = ref('');
  const pendingEmailQuery = ref('');

  const rateLimitOverride = getIntegerQueryParameter({
    queryKey: 'rateLimitOverride',
    route,
    minValue: 0,
  });
  const freeQueryLimit = rateLimitOverride ?? FREE_QUERY_LIMIT;

  const shouldClearConversationFromQuery = (): boolean => {
    const clearConversationIdParam = getBooleanQueryParameter({
      queryKey: 'clearConversationId',
      route,
    });
    if (clearConversationIdParam === null) {
      return false;
    }

    return clearConversationIdParam;
  };

  const shouldClearRemoteLimit = (): boolean => {
    const isDev = useEnvContext().isDev;
    if (!isDev) return false;
    const willClear =
      getBooleanQueryParameter({ queryKey: 'clearRemoteRateLimit', route }) === true;
    return willClear;
  };

  const setAdvisorConversationSession = ({ conversationId }: { conversationId: string }): void => {
    advisorSessionId.value = conversationId;

    const current = getFromSessionStorageOrNull<AdvisorSessionStorage>({
      key: aiAdvisorSessionStorageKey,
    });

    setToSessionStorage<AdvisorSessionStorage>({
      key: aiAdvisorSessionStorageKey,
      data: {
        query: current?.query,
        numQueries: current?.numQueries,
        conversationId,
      },
      ttl: aiAdvisorSessionStorageTtl,
    });
  };

  const getNumQueriesFromStorage = (): number => {
    const stored = getFromSessionStorageOrNull<AdvisorSessionStorage>({
      key: aiAdvisorSessionStorageKey,
    });
    return stored?.numQueries ?? 0;
  };

  const incrementNumQueriesInStorage = (): void => {
    const stored = getFromSessionStorageOrNull<AdvisorSessionStorage>({
      key: aiAdvisorSessionStorageKey,
    });
    const currentNumQueries = stored?.numQueries ?? 0;

    setToSessionStorage<AdvisorSessionStorage>({
      key: aiAdvisorSessionStorageKey,
      data: {
        query: stored?.query,
        numQueries: currentNumQueries + 1,
        conversationId: stored?.conversationId ?? advisorSessionId.value,
      },
      ttl: aiAdvisorSessionStorageTtl,
    });
  };

  const clearAdvisorConversationSession = (): void => {
    advisorSessionId.value = '';
    conversationStartPromise.value = null;
    setToSessionStorage<AdvisorSessionStorage | null>({
      key: aiAdvisorSessionStorageKey,
      data: null,
    });
  };

  // Reset query count when user logs in (from logged-out state)
  const resetQueryCountOnLogin = (): void => {
    const stored = getFromSessionStorageOrNull<AdvisorSessionStorage>({
      key: aiAdvisorSessionStorageKey,
    });

    setToSessionStorage<AdvisorSessionStorage>({
      key: aiAdvisorSessionStorageKey,
      data: {
        query: stored?.query,
        numQueries: 0,
        conversationId: stored?.conversationId ?? advisorSessionId.value,
      },
      ttl: aiAdvisorSessionStorageTtl,
    });
  };

  // Watch for login status changes and reset query count when user logs in
  watch(
    () => isLoggedIn.value,
    (newLoginStatus, oldLoginStatus) => {
      if (
        newLoginStatus === 'loggedin' &&
        (oldLoginStatus === 'loggedout' || oldLoginStatus === 'unknown')
      ) {
        resetQueryCountOnLogin();
      }
    }
  );

  if (shouldClearConversationFromQuery()) {
    clearAdvisorConversationSession();
  } else {
    const initialConversationId = getStringQueryParameter({ queryKey: 'conversationId', route });
    if (initialConversationId) {
      setAdvisorConversationSession({ conversationId: initialConversationId });
    }
  }

  // Analytics expects string flags; centralize the conversions used across events.
  const loggedInFlag = (): string => (isLoggedIn.value === 'loggedin' ? 'true' : 'false');
  const getUserEmail = (): string => user.value?.email || sessionEmail.value || ANONYMOUS_EMAIL;

  const trackFailure = ({
    failureEvent,
    err,
    sentryLabel,
    sentryTag,
  }: TrackFailureOptions): void => {
    const errorMessage = CatchUtil.getMessage(err);

    dataLayerPush({
      event: failureEvent,
      msg: errorMessage,
      isLoggedIn: loggedInFlag(),
    });

    mouseFlowPush(['tag', AI_SEARCH_MOUSEFLOW_ERROR_TAG]);

    ErrorUtil.safePostMessageToSentry({
      tag: sentryTag,
      msg: `${sentryLabel}: ${errorMessage}`,
      error: err,
    });
  };

  const persistAdvisorSessionId = ({ conversationId }: { conversationId: string }) => {
    setAdvisorConversationSession({ conversationId });
  };

  // Pre-fetches a conversation ID as soon as the user starts typing.
  const onConversationStart = ({ isFromSearchBar = false }: { isFromSearchBar?: boolean }) => {
    // Only pre-fetch once and only if we don't already have a session ID
    if (advisorSessionId.value || conversationStartPromise.value) return;

    // Declared before assignment so the IIFE's finally can compare against itself.
    let prefetchPromise: Promise<void> | null = null;
    prefetchPromise = (async () => {
      try {
        const aiQueryEndpoint = await mapFbFunction('/api/fb/aiQuery');

        const token = await AntiSpamUtil.waitForNonExpiredCsrfToken();
        if (!token) return;

        const response = await useWwwFetch<AiAdvisorResponse>({
          url: aiQueryEndpoint,
          body: {
            new: true,
            csrfToken: token,
            signedScore: AntiSpamUtil.getSignedScore(),
            ...AntiSpamUtil.getTimings(),
            email: getUserEmail(),
          },
          method: 'POST',
        });

        if (!response.isSuccess) {
          console.error('Failed to pre-fetch conversation ID');
          trackFailure({
            failureEvent: 'AiSearchPrefetchFailed',
            err: response.msg,
            sentryLabel: 'AiAdvisor prefetch failed',
            sentryTag: ErrorTagUtil.aiSearchPrefetch,
          });
          return;
        }

        const parsedResponse = parseQueryResponse(response);
        if (parsedResponse.conversationId) {
          persistAdvisorSessionId({
            conversationId: parsedResponse.conversationId,
          });
        }

        mouseFlowPush(['tag', 'ai-advisor-session']);
        dataLayerPush({
          event: 'AiSearchConversationStarted',
          isFromSearchBar: String(isFromSearchBar),
          isLoggedIn: loggedInFlag(),
        });
      } catch (err) {
        console.error('Error pre-fetching conversation ID:', err);
        trackFailure({
          failureEvent: 'AiSearchPrefetchFailed',
          err,
          sentryLabel: 'AiAdvisor prefetch exception',
          sentryTag: ErrorTagUtil.aiSearchPrefetch,
        });
      } finally {
        // Always clear so later calls can retry if prefetch did not establish a session.
        if (conversationStartPromise.value === prefetchPromise) {
          conversationStartPromise.value = null;
        }
      }
    })();

    conversationStartPromise.value = prefetchPromise;
  };

  const consumeInitialQueryFromSessionStorage = () => {
    const stored = getFromSessionStorageOrNull<AdvisorSessionStorage>({
      key: aiAdvisorSessionStorageKey,
    });

    if (!stored) return '';

    const { query: storedQuery } = stored;

    let dataAfterConsume: AdvisorSessionStorage | null = null;
    if (typeof stored.conversationId === 'string' && typeof storedQuery === 'string') {
      dataAfterConsume = {
        ...stored,
        query: undefined,
      };
    }

    setToSessionStorage<AdvisorSessionStorage | null>({
      key: aiAdvisorSessionStorageKey,
      data: dataAfterConsume,
      ttl: aiAdvisorSessionStorageTtl,
    });

    return typeof storedQuery === 'string' ? storedQuery.trim() : '';
  };

  // Restores shared session IDs from session storage (call during onMounted).
  const restoreSessionFromStorage = () => {
    const stored = getFromSessionStorageOrNull<AdvisorSessionStorage>({
      key: aiAdvisorSessionStorageKey,
    });

    if (stored && typeof stored.conversationId === 'string') {
      advisorSessionId.value = stored.conversationId;
    }
  };

  // Stores a search query alongside existing session IDs before navigating to the advisor page.
  const storeSearchQuery = (searchTerm: string) => {
    const stored = getFromSessionStorageOrNull<AdvisorSessionStorage>({
      key: aiAdvisorSessionStorageKey,
    });

    setToSessionStorage<AdvisorSessionStorage>({
      key: aiAdvisorSessionStorageKey,
      data: {
        query: searchTerm,
        numQueries: stored?.numQueries,
        conversationId: advisorSessionId.value,
      },
      ttl: aiAdvisorSessionStorageTtl,
    });
  };

  const scrollToBottom = () => {
    nextTick(() => {
      if (chatBox?.value) {
        chatBox.value.scrollTop = chatBox.value.scrollHeight;
      }
    });
  };

  const {
    stopEllipsisAnimation,
    typeText,
    stopTyping,
    cleanup: cleanupAnimation,
  } = useTypewriterAnimation<AiAdvisorChatEntry>(scrollToBottom, {
    getText: (entry) => entry.message.content.text ?? '',
    setText: (entry, text) => {
      if (!entry.message.content) {
        entry.message.content = { text };
        return;
      }

      entry.message.content.text = text;
    },
  });

  const stopRequest = (): void => {
    lastStoppedRequestId.value = latestRequestId.value;
    stopTyping();
    stopEllipsisAnimation();

    for (const chatEntry of chatHistory.value) {
      if (chatEntry.message.role !== 'assistant') continue;

      chatEntry.isStreaming = false;
    }

    const lastEntry = chatHistory.value[chatHistory.value.length - 1];
    const lastEntryText = lastEntry?.message.content.text?.trim() ?? '';
    if (
      lastEntry &&
      lastEntry.message.role === 'assistant' &&
      !lastEntryText &&
      lastEntry.retrievalResults.length === 0
    ) {
      chatHistory.value.pop();
    }

    isQueryLoading.value = false;
  };

  const getLastUserQueryText = (): string => {
    for (let entryIndex = chatHistory.value.length - 1; entryIndex >= 0; entryIndex -= 1) {
      const chatEntry = chatHistory.value[entryIndex];
      if (chatEntry?.message.role !== 'user') continue;

      return chatEntry.message.content.text?.trim() ?? '';
    }

    return '';
  };

  const getRetrievalResultKey = ({
    retrievalResult,
  }: {
    retrievalResult: AiAdvisorRetrievalResult;
  }): string => {
    const url = retrievalResult.location.webLocation.url;
    const title = retrievalResult.metadata.title ?? '';
    const text = retrievalResult.content.text;
    return `${url}|${title}|${text}`;
  };

  // Returns all retrieval results that have not yet been presented to the user in the chat history.
  const getUnpresentedRetrievalContext = (): AiAdvisorRetrievalResult[] => {
    const presentedKeys = new Set<string>();

    for (const chatEntry of chatHistory.value) {
      if (chatEntry.message.role !== 'assistant') continue;

      const visibleResults = chatEntry.retrievalResults.slice(0, chatEntry.visibleRetrievalCount);
      for (const retrievalResult of visibleResults) {
        presentedKeys.add(getRetrievalResultKey({ retrievalResult }));
      }
    }

    const unseenResults: AiAdvisorRetrievalResult[] = [];
    const unseenKeys = new Set<string>();

    for (const chatEntry of chatHistory.value) {
      if (chatEntry.message.role !== 'assistant') continue;

      for (const retrievalResult of chatEntry.retrievalResults) {
        const retrievalKey = getRetrievalResultKey({ retrievalResult });
        if (presentedKeys.has(retrievalKey) || unseenKeys.has(retrievalKey)) {
          continue;
        }

        unseenResults.push(retrievalResult);
        unseenKeys.add(retrievalKey);
      }
    }

    return unseenResults;
  };

  /**
   * Shared request lifecycle for every advisor query (initial send, retry, show-more).
   *
   * Handles request-id tracking, prefetch await, CSRF token, client-side rate limiting,
   * the API call, response mapping, typewriter streaming, analytics, and cleanup. Callers
   * only supply the bits that differ between flows via {@link ExecuteQueryOptions}.
   */
  const executeQuery = async ({
    queryText,
    analyticsEvent,
    failureEvent,
    errorLogLabel,
    pushUserMessage,
    popLastAssistant,
    clearQueryOnFinish,
    bodyOverrides,
    extraAnalytics,
  }: ExecuteQueryOptions): Promise<void> => {
    const shouldPushUserMessage = pushUserMessage ?? false;
    const shouldPopLastAssistant = popLastAssistant ?? false;
    const shouldClearQueryOnFinish = clearQueryOnFinish ?? false;
    const safeBodyOverrides = bodyOverrides ?? {};
    const safeExtraAnalytics = extraAnalytics ?? {};

    latestRequestId.value += 1;
    const requestId = latestRequestId.value;
    isQueryLoading.value = true;

    // Assigned once the assistant placeholder exists; guards cleanup on early returns.
    let reactiveAiResponseMsg: AiAdvisorChatEntry | null = null;

    try {
      // Wait for the pre-fetch to resolve before sending the query so the session ID is available
      if (!advisorSessionId.value && conversationStartPromise.value) {
        await conversationStartPromise.value;
        if (isRequestCancelled({ requestId })) return;
      }

      scrollToBottom();

      const aiQueryEndpoint = await mapFbFunction('/api/fb/aiQuery');

      // Add the user's query to the chat history for normal sends.
      if (shouldPushUserMessage) {
        chatHistory.value.push(
          createChatEntry({
            message: {
              role: 'user',
              content: { text: queryText },
            },
          })
        );
      }

      // Drop the previous assistant answer before retrying it.
      if (shouldPopLastAssistant) {
        const lastEntry = chatHistory.value[chatHistory.value.length - 1];
        if (lastEntry && lastEntry.message.role === 'assistant') {
          chatHistory.value.pop();
        }
      }

      // Reserve a dedicated assistant entry so response/error text never mutates the user message.
      chatHistory.value.push(createEmptyAssistantEntry());
      reactiveAiResponseMsg = chatHistory.value[chatHistory.value.length - 1]!;

      const token = await AntiSpamUtil.waitForNonExpiredCsrfToken();
      if (isRequestCancelled({ requestId })) return;

      if (!token) {
        reactiveAiResponseMsg.isStreaming = false;
        await typeText({
          target: reactiveAiResponseMsg,
          fullText: errorMessages?.missingTokenMessage ?? MISSING_TOKEN_MESSAGE,
        });
        return;
      }

      const body = buildQueryBody({
        queryText,
        sessionId: advisorSessionId.value,
        numQueries: getNumQueriesFromStorage(),
        csrfToken: token,
        email: getUserEmail(),
        ...(shouldClearRemoteLimit() && { clearRateLimits: true }),
        ...safeBodyOverrides,
      });

      const rateLimit = checkRateLimit({
        loginStatus: isLoggedIn.value,
        numQueries: body.numQueries,
        freeQueryLimit,
        rateLimitMessage: errorMessages?.rateLimitMessage ?? RATE_LIMIT_MESSAGE,
      });

      if (rateLimit.isRateLimited) {
        dataLayerPush({
          event: 'AiSearchRateLimited',
          isLoggedIn: loggedInFlag(),
          numQueries: String(body.numQueries),
        });
        mouseFlowPush(['tag', AI_SEARCH_MOUSEFLOW_RATE_LIMIT_TAG]);

        reactiveAiResponseMsg.isStreaming = false;
        await typeText({
          target: reactiveAiResponseMsg,
          fullText: errorMessages?.rateLimitMessage ?? RATE_LIMIT_MESSAGE,
        });
        return;
      }

      dataLayerPush({
        event: analyticsEvent,
        isLoggedIn: loggedInFlag(),
        numQueries: String(body.numQueries),
        ...safeExtraAnalytics,
      });

      const response = await useWwwFetch<AiAdvisorResponse>({
        url: aiQueryEndpoint,
        method: 'POST',
        body,
      });
      if (isRequestCancelled({ requestId })) return;

      if (!response.isSuccess) {
        if (response.isRateLimited === 'emailRequired') {
          pendingEmailQuery.value = queryText;
          reactiveAiResponseMsg.isStreaming = false;
          reactiveAiResponseMsg.needsEmailCapture = true;
          await typeText({
            target: reactiveAiResponseMsg,
            fullText: response.response ?? REQUEST_ERROR_MESSAGE,
          });
          return;
        }

        reactiveAiResponseMsg.isStreaming = false;
        await typeText({
          target: reactiveAiResponseMsg,
          fullText: response.response ?? REQUEST_ERROR_MESSAGE,
        });
        return;
      }

      // Increment query count for new queries (not retries or show-more requests)
      const isNewQuery = !safeBodyOverrides.tryAgain && !safeBodyOverrides.showMoreResults;
      if (isNewQuery) {
        incrementNumQueriesInStorage();
      }

      const parsedResponse = parseQueryResponse(response);

      if (parsedResponse.conversationId) {
        persistAdvisorSessionId({
          conversationId: parsedResponse.conversationId,
        });
      }

      detectedIntent.value = parsedResponse.detectedIntent;
      detectedLanguage.value = parsedResponse.detectedLanguage;

      applyRetrievalResults({ entry: reactiveAiResponseMsg, parsedResponse });

      if (isRequestCancelled({ requestId })) return;
      reactiveAiResponseMsg.isStreaming = false;
      await typeText({ target: reactiveAiResponseMsg, fullText: parsedResponse.responseText });
    } catch (err) {
      if (isRequestCancelled({ requestId })) return;
      console.error(errorLogLabel, err);

      let sentryTag: Parameters<typeof ErrorUtil.safePostMessageToSentry>[0]['tag'] =
        ErrorTagUtil.aiSearchRequest;
      if (failureEvent === 'AiSearchShowMoreResultsFailed') {
        sentryTag = ErrorTagUtil.aiSearchShowMoreResults;
      } else if (failureEvent === 'AiSearchRetryFailed') {
        sentryTag = ErrorTagUtil.aiSearchRetry;
      }

      trackFailure({
        failureEvent,
        err,
        sentryLabel: errorLogLabel,
        sentryTag,
      });

      if (reactiveAiResponseMsg) {
        reactiveAiResponseMsg.isStreaming = false;
        await typeText({
          target: reactiveAiResponseMsg,
          fullText: errorMessages?.requestErrorMessage ?? REQUEST_ERROR_MESSAGE,
        });
      }
    } finally {
      // Skip cleanup when a newer request (or stop) superseded this one; that owner resets state.
      if (!isRequestCancelled({ requestId })) {
        if (reactiveAiResponseMsg) {
          reactiveAiResponseMsg.isStreaming = false;
        }
        stopEllipsisAnimation();
        if (shouldClearQueryOnFinish) {
          query.value = ''; // Clear input field
        }
        isQueryLoading.value = false;
      }
    }
  };

  const makeReq = async (): Promise<void> => {
    if (!trimmedQuery.value || isQueryLoading.value) return; // Prevent empty queries or concurrent calls

    await executeQuery({
      queryText: trimmedQuery.value,
      pushUserMessage: true,
      clearQueryOnFinish: true,
      analyticsEvent: 'AiSearchInteraction',
      failureEvent: 'AiSearchRequestFailed',
      errorLogLabel: 'Error making request:',
    });
  };

  const loadMoreResults = async ({ entryIndex }: { entryIndex: number }): Promise<void> => {
    const targetEntry = chatHistory.value[entryIndex];
    if (!targetEntry || targetEntry.message.role !== 'assistant' || isQueryLoading.value) return;

    if (!targetEntry.hasMoreRetrievalResults) return;

    const queryText = getLastUserQueryText();
    if (!queryText) return;

    await executeQuery({
      queryText,
      bodyOverrides: {
        showMoreResults: true,
        retrievedContext: getUnpresentedRetrievalContext(),
      },
      analyticsEvent: 'AiSearchShowMoreResults',
      failureEvent: 'AiSearchShowMoreResultsFailed',
      errorLogLabel: 'Error showing more results:',
    });
  };

  const retryLastQuery = async (): Promise<void> => {
    const queryText = trimmedQuery.value || getLastUserQueryText();
    if (!queryText || isQueryLoading.value) return; // Prevent empty queries or concurrent calls

    await executeQuery({
      queryText,
      popLastAssistant: true,
      bodyOverrides: {
        tryAgain: true,
        retrievedContext: getUnpresentedRetrievalContext(),
      },
      analyticsEvent: 'AiSearchRetry',
      extraAnalytics: { isRetry: 'true' },
      failureEvent: 'AiSearchRetryFailed',
      errorLogLabel: 'Error retrying request:',
    });
  };

  const submitEmailAndReplay = async ({
    email,
  }: {
    email: string;
  }): Promise<{ validationError: string } | null> => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      return { validationError: 'Please enter your email address.' };
    }
    if (!isValidEmailFormat(trimmedEmail)) {
      return { validationError: 'Please enter a valid email address.' };
    }

    const queryText = pendingEmailQuery.value;
    if (!queryText || isQueryLoading.value) return null;

    sessionEmail.value = trimmedEmail;
    pendingEmailQuery.value = '';

    const lastEntry = chatHistory.value[chatHistory.value.length - 1];
    if (lastEntry && lastEntry.message.role === 'assistant') {
      lastEntry.needsEmailCapture = false;
    }

    await executeQuery({
      queryText,
      popLastAssistant: true,
      analyticsEvent: 'AiSearchInteraction',
      failureEvent: 'AiSearchRequestFailed',
      errorLogLabel: 'Error replaying query after email gate:',
    });

    return null;
  };

  return {
    query,
    chatHistory,
    isQueryLoading,
    trimmedQuery,
    cleanupAnimation,
    stopRequest,
    makeReq,
    retryLastQuery,
    loadMoreResults,
    onConversationStart,
    consumeInitialQueryFromSessionStorage,
    restoreSessionFromStorage,
    storeSearchQuery,
    advisorSessionId,
    detectedIntent,
    detectedLanguage,
    pendingEmailQuery,
    submitEmailAndReplay,
  };
};
